import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, Outlet, useNavigate } from "react-router";
import { useEdgeSwipeBack } from "../mobile/gestures";
import { createPortal } from "react-dom";
import { Drawer, Icon, IconButton, Select, useLayout } from "@agentsaas/ui";
import { SUPPORTED_LOCALES, type Locale } from "@agentsaas/protocol";
import type { ConnectionState } from "@agentsaas/client-sdk";
import { useT } from "../app/i18n";
import { useThemePref, resolveTheme } from "../app/theme";
import { useServices } from "../app/services";
import { useSetLocale } from "../app/session";
import { NotificationBell } from "../notifications/NotificationCenter";
import { OfflineBanner, PwaBridge } from "../pwa/register";

/**
 * 应用外壳：顶栏 + 内容区。
 * 响应式（doc/dev_plan.md §1.2）：≥1280 三栏；768–1279 两栏 + 抽屉（左栏进抽屉）；<768 单栏 + 底部 tab。
 * 平板时 Workspace 把左栏放进抽屉，顶栏显示菜单按钮；页面可用 ShellSlot 往顶栏插内容。
 */

interface ShellCtx {
  hasDrawer: boolean;
  setHasDrawer(v: boolean): void;
  drawerOpen: boolean;
  setDrawerOpen(v: boolean): void;
  /** 顶栏中的插槽（页面通过 ShellSlot 以 portal 渲染进去，避免状态回环） */
  titleEl: HTMLElement | null;
  actionsEl: HTMLElement | null;
  setTitleRef(el: HTMLElement | null): void;
  setActionsRef(el: HTMLElement | null): void;
}
const Ctx = createContext<ShellCtx | null>(null);

export function useShell(): ShellCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error("AppFrame missing");
  return c;
}

/** 把内容渲染到顶栏的标题区 / 操作区 */
export function ShellSlot({ name, children }: { name: "title" | "actions"; children: ReactNode }) {
  const shell = useContext(Ctx);
  const el = name === "title" ? shell?.titleEl : shell?.actionsEl;
  return el ? createPortal(children, el) : null;
}

function useConnection(): ConnectionState {
  const { ws } = useServices();
  const [s, setS] = useState(ws.state);
  useEffect(() => ws.onStateChange(setS), [ws]);
  return s;
}

export function LanguageSelect({ compact, id }: { compact?: boolean; id?: string }) {
  const { t, locale } = useT();
  const setLocale = useSetLocale();
  return (
    <Select
      id={id}
      aria-label={t("settings.language")}
      className={compact ? "shell-lang" : undefined}
      value={locale}
      onChange={(e) => void setLocale(e.target.value as Locale)}
      data-testid="lang-select"
    >
      {SUPPORTED_LOCALES.map((l) => (
        <option key={l} value={l}>
          {t(`locale.${l}`)}
        </option>
      ))}
    </Select>
  );
}

export function ThemeToggle() {
  const { t } = useT();
  const [pref, setPref] = useThemePref();
  const cur = resolveTheme(pref);
  const next = cur === "dark" ? "light" : "dark";
  return <IconButton icon={cur === "dark" ? "sun" : "moon"} label={t(`theme.${next}`)} onClick={() => setPref(next)} data-testid="theme-toggle" />;
}

export function TopBar() {
  const { t } = useT();
  const layout = useLayout();
  const shell = useShell();
  const conn = useConnection();
  return (
    <header className="shell-top">
      {shell.hasDrawer && layout === "tablet" ? <IconButton icon="menu" label={t("nav.menu")} onClick={() => shell.setDrawerOpen(true)} aria-haspopup="dialog" /> : null}
      <Link to="/" className="shell-brand">
        {t("app.name")}
      </Link>
      <div className="shell-title" ref={shell.setTitleRef} />
      <div className="shell-actions">
        <div className="shell-slot" ref={shell.setActionsRef} />
        <NotificationBell />
        {layout !== "mobile" ? <LanguageSelect compact /> : null}
        <ThemeToggle />
        {layout !== "mobile" ? (
          <Link to="/settings" className="ui-iconbtn" aria-label={t("nav.settings")} title={t("nav.settings")}>
            <Icon name="settings" />
          </Link>
        ) : null}
      </div>
      {conn === "reconnecting" ? (
        <div className="shell-conn" role="status">
          {t("shell.connection.reconnecting")}
        </div>
      ) : null}
    </header>
  );
}

/** 已登录区域的外框 */
export function AppFrame({ children }: { children?: ReactNode }) {
  const { t } = useT();
  const layout = useLayout();
  const [hasDrawer, setHasDrawer] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [titleEl, setTitleRef] = useState<HTMLElement | null>(null);
  const [actionsEl, setActionsRef] = useState<HTMLElement | null>(null);
  const value = useMemo(
    () => ({ hasDrawer, setHasDrawer, drawerOpen, setDrawerOpen, titleEl, actionsEl, setTitleRef, setActionsRef }),
    [hasDrawer, drawerOpen, titleEl, actionsEl],
  );
  const navigate = useNavigate();
  const goBack = useMemo(() => () => navigate(-1), [navigate]);
  useEdgeSwipeBack(goBack, { enabled: layout === "mobile" });
  // 离开平板档位时关闭抽屉
  useEffect(() => {
    if (layout !== "tablet") setDrawerOpen(false);
  }, [layout]);
  return (
    <Ctx.Provider value={value}>
      <div className={`shell shell--${layout}`} data-layout={layout}>
        <a href="#main" className="shell-skip">
          {t("shell.skip_to_content")}
        </a>
        <TopBar />
        <OfflineBanner />
        <PwaBridge />
        <main id="main" className="shell-main" tabIndex={-1}>
          {children ?? <Outlet />}
        </main>
      </div>
    </Ctx.Provider>
  );
}

/** IDE 工作区：左栏 / 主区 / 右栏；平板时左栏进抽屉；手机时由 mobile 接管（单栏 + 底部 tab） */
export function Workspace({ sidebar, main, aside, mobile }: { sidebar: ReactNode; main: ReactNode; aside: ReactNode; mobile?: ReactNode }) {
  const { t } = useT();
  const layout = useLayout();
  const shell = useShell();
  const { setHasDrawer, setDrawerOpen } = shell;
  useEffect(() => {
    setHasDrawer(layout === "tablet");
    return () => setHasDrawer(false);
  }, [layout, setHasDrawer]);
  if (layout === "mobile" && mobile) return <div className="ws ws--mobile">{mobile}</div>;
  return (
    <div className={`ws ws--${layout}`}>
      {layout === "desktop" ? <aside className="ws-left">{sidebar}</aside> : null}
      <section className="ws-main">{main}</section>
      <aside className="ws-right">{aside}</aside>
      {layout === "tablet" ? (
        <Drawer open={shell.drawerOpen} onClose={() => setDrawerOpen(false)} title={t("nav.menu")} closeLabel={t("shell.close")}>
          <div onClick={(e) => (e.target as HTMLElement).closest("a,[data-close-drawer]") && setDrawerOpen(false)}>{sidebar}</div>
        </Drawer>
      ) : null}
    </div>
  );
}
